/**
 * Goods Intake (tidaltech/koi#8100, #8101): goods expected at a Site, counted
 * on arrival and posted into stock. Posting records every shortage, overage
 * and damage; it never corrects a count to what was expected.
 */

export const receiptStates = ['EXPECTED', 'RECEIVING', 'POSTED', 'CANCELLED'] as const;
export type ReceiptState = (typeof receiptStates)[number];

export const receiptStateLabels: Record<ReceiptState, string> = {
  EXPECTED: 'Expected',
  RECEIVING: 'Counting',
  POSTED: 'Received',
  CANCELLED: 'Cancelled',
};

export const receiptSources = [
  'MANUAL',
  'EDI',
  'PEPPOL',
  'SUPPLIER_API',
  'CARGONIZER',
  'OCR',
] as const;
export type ReceiptSource = (typeof receiptSources)[number];

export const receiptSourceLabels: Record<ReceiptSource, string> = {
  MANUAL: 'Entered by hand',
  EDI: 'EDI',
  PEPPOL: 'Peppol',
  SUPPLIER_API: 'Supplier API',
  CARGONIZER: 'Cargonizer',
  OCR: 'Scanned delivery note',
};

export const discrepancyKinds = ['SHORTAGE', 'OVERAGE', 'DAMAGED'] as const;
export type DiscrepancyKind = (typeof discrepancyKinds)[number];

export const discrepancyKindLabels: Record<DiscrepancyKind, string> = {
  SHORTAGE: 'Short',
  OVERAGE: 'Extra',
  DAMAGED: 'Damaged',
};

export interface ExpectedReceiptLine {
  id: string;
  stockItemId: string;
  /** Zero for goods that arrived without being on the manifest. */
  expectedQuantity: number;
  /** Undefined until counted; includes damaged units. */
  receivedQuantity?: number | undefined;
  damagedQuantity?: number | undefined;
  locationId?: string | undefined;
  note?: string | undefined;
  movementId?: string | undefined;
}

export interface ReceiptDiscrepancy {
  lineId?: string | undefined;
  stockItemId: string;
  kind: DiscrepancyKind;
  quantity: number;
  note?: string | undefined;
}

export interface ExpectedReceipt {
  id: string;
  siteId: string;
  supplier?: string | undefined;
  supplierReference?: string | undefined;
  identifiers: string[];
  source: ReceiptSource;
  expectedAt?: string | undefined;
  notes?: string | undefined;
  lines: ExpectedReceiptLine[];
  state: ReceiptState;
  discrepancies: ReceiptDiscrepancy[];
  cancelReason?: string | undefined;
  postedBy?: string | undefined;
  postedAt?: string | undefined;
  createdBy?: string | undefined;
  createTime?: string | undefined;
  version: number;
  lastModified?: string | undefined;
}

export interface ExpectedReceiptInput {
  siteId: string;
  supplier?: string | undefined;
  supplierReference?: string | undefined;
  identifiers: string[];
  expectedAt?: string | undefined;
  notes?: string | undefined;
  lines: { stockItemId: string; expectedQuantity: number }[];
}

/** One line's count. Names a line by id, or a stock item not yet on the receipt. */
export interface ReceiptCount {
  lineId?: string | undefined;
  stockItemId?: string | undefined;
  receivedQuantity: number;
  damagedQuantity: number;
  locationId?: string | undefined;
  note?: string | undefined;
}

export interface PostReceiptResult {
  receipt: ExpectedReceipt;
  /** The receipt was already posted; nothing changed. */
  replayed: boolean;
}

export function isOpenReceipt(receipt: Pick<ExpectedReceipt, 'state'>): boolean {
  return receipt.state === 'EXPECTED' || receipt.state === 'RECEIVING';
}

/** Same rule as the server: letters and digits only, uppercased. */
export function normalizeReceiptCode(code: string): string {
  return code.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/** What posting this line would record. */
export function lineDiscrepancies(
  line: Pick<ExpectedReceiptLine, 'expectedQuantity' | 'receivedQuantity' | 'damagedQuantity'>,
): { kind: DiscrepancyKind; quantity: number }[] {
  if (line.receivedQuantity === undefined) return [];
  const out: { kind: DiscrepancyKind; quantity: number }[] = [];
  const received = line.receivedQuantity;
  if (received < line.expectedQuantity) {
    out.push({ kind: 'SHORTAGE', quantity: line.expectedQuantity - received });
  } else if (received > line.expectedQuantity) {
    out.push({ kind: 'OVERAGE', quantity: received - line.expectedQuantity });
  }
  const damaged = line.damagedQuantity ?? 0;
  if (damaged > 0) out.push({ kind: 'DAMAGED', quantity: damaged });
  return out;
}

/** Units that go into stock: received minus damaged. */
export function acceptedQuantity(
  line: Pick<ExpectedReceiptLine, 'receivedQuantity' | 'damagedQuantity'>,
): number {
  return Math.max(0, (line.receivedQuantity ?? 0) - (line.damagedQuantity ?? 0));
}

/** Why the receipt can't be posted yet, or null when it can. */
export function postBlocker(receipt: ExpectedReceipt): string | null {
  if (!isOpenReceipt(receipt)) return 'This receipt is closed.';
  const uncounted = receipt.lines.filter((line) => line.receivedQuantity === undefined).length;
  if (uncounted > 0) {
    return uncounted === 1
      ? '1 line isn’t counted yet. Record 0 if nothing arrived.'
      : `${uncounted} lines aren’t counted yet. Record 0 for anything that didn’t arrive.`;
  }
  const homeless = receipt.lines.filter((line) => acceptedQuantity(line) > 0 && !line.locationId);
  if (homeless.length > 0) return 'Choose where every accepted line goes.';
  return null;
}

/** Validates one count before it's sent. */
export function validateCount(count: ReceiptCount): string | null {
  const { receivedQuantity: received, damagedQuantity: damaged } = count;
  if (!Number.isInteger(received) || received < 0) return 'Received must be 0 or more.';
  if (!Number.isInteger(damaged) || damaged < 0) return 'Damaged must be 0 or more.';
  if (damaged > received) return 'Damaged can’t be more than received.';
  return null;
}

export function validateReceiptInput(input: ExpectedReceiptInput): string | null {
  if (!input.siteId) return 'Choose the receiving site.';
  if (input.lines.length === 0) return 'Add at least one line.';
  for (const [index, line] of input.lines.entries()) {
    if (!line.stockItemId) return `Line ${index + 1}: choose a stock item.`;
    if (!Number.isInteger(line.expectedQuantity) || line.expectedQuantity <= 0) {
      return `Line ${index + 1}: the quantity must be a whole number above 0.`;
    }
  }
  const ids = input.lines.map((line) => line.stockItemId);
  if (new Set(ids).size !== ids.length) return 'Each stock item can only be on one line.';
  return null;
}
